/**
 * Onboarding Lifecycle API — the tenant onboarding state machine.
 *
 * TWO SCOPES
 * ----------
 *   - TENANT-scoped (owner): submit + read the CURRENT tenant's submission. The
 *     tenant is resolved from the caller's own owner membership, never a path
 *     parameter.
 *   - PLATFORM-scoped (`tenant.update`): review + activate a NAMED tenant's
 *     submission, selected by path parameter.
 *
 * CONTRACT SOURCE: contracts/openapi/foundation.v1.yaml (register v1.4).
 * Every response is parsed by its Zod schema at the transport boundary.
 */
import { http, generateIdempotencyKey } from '@/api/client/http';
import {
  OnboardingSubmissionReadResponseSchema,
  OnboardingSubmissionSuccessResponseSchema,
} from '@/api/schemas/onboarding-lifecycle';
import type {
  OnboardingSubmission,
  ReadinessSnapshot,
  ReviewOnboardingRequest,
} from '@/api/schemas/onboarding-lifecycle';

const CURRENT = '/tenants/current/onboarding/submission';

export const OnboardingLifecycleApi = {
  // -------------------------------------------------------------------------
  // TENANT-scoped (owner)
  // -------------------------------------------------------------------------

  /** POST /tenants/current/onboarding/submission — submit the current tenant for review. */
  async submit(readinessSnapshot: ReadinessSnapshot): Promise<OnboardingSubmission> {
    const response = await http.post(CURRENT, OnboardingSubmissionSuccessResponseSchema, {
      body: { readinessSnapshot },
      idempotencyKey: generateIdempotencyKey(),
    });
    return response.data;
  },

  /** GET /tenants/current/onboarding/submission — the current tenant's latest submission (or null). */
  async getCurrent(): Promise<OnboardingSubmission | null> {
    const response = await http.get(CURRENT, OnboardingSubmissionReadResponseSchema);
    return response.data;
  },

  // -------------------------------------------------------------------------
  // PLATFORM-scoped (tenant.update)
  // -------------------------------------------------------------------------

  /** POST /tenants/{tenantId}/onboarding/submission/{submissionId}/review — approve/reject/request info. */
  async review(
    tenantId: string,
    submissionId: string,
    input: ReviewOnboardingRequest,
  ): Promise<OnboardingSubmission> {
    const response = await http.post(
      `/tenants/${tenantId}/onboarding/submission/${submissionId}/review`,
      OnboardingSubmissionSuccessResponseSchema,
      { body: input, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  /** POST /tenants/{tenantId}/onboarding/submission/{submissionId}/activate — activate an approved submission. */
  async activate(
    tenantId: string,
    submissionId: string,
    expectedVersion: number,
  ): Promise<OnboardingSubmission> {
    const response = await http.post(
      `/tenants/${tenantId}/onboarding/submission/${submissionId}/activate`,
      OnboardingSubmissionSuccessResponseSchema,
      { body: { expectedVersion }, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },
};

export type { OnboardingSubmission, ReadinessSnapshot, ReviewOnboardingRequest };
